import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { AfterViewInit, Component, DestroyRef, ElementRef, Input, OnInit, ViewChild, inject, input, numberAttribute } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, AsyncValidatorFn, ControlContainer, FormControl, FormsModule, NgForm, NgModel, ValidationErrors } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FRCWidgetCompleteEvent, FRCWidgetWidgetErrorEvent, FriendlyCaptchaSDK, WidgetHandle } from "@friendlycaptcha/sdk";
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable, Subject, debounceTime, distinctUntilChanged, finalize, of, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { APPOINTMENT_CONSTANT } from '../../../constants/appointment-constants';
import { DATE_FORMATS } from '../../../constants/date.constants';
import { TEMPLATE_CONSTANT } from '../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { CAPTCHA_TYPE, CUSTOMER_CSTM_FIELD_TITLE_LIST, MEETING_TYPE_ID, REGEX, SALUTATION_TYPES, WIDGET_CONSTANTS } from '../../../constants/widget-constants';
import { CustomerDbModel } from '../../../db-models/appointment-custom-fields-dao';
import { StoresDao } from '../../../db-models/stores-dao';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { ZipCodeItem } from '../../../db-models/zip-code.model';
import { CartItem } from '../../../models/cart.model';
import { CountryDbModel } from '../../../models/country.model';
import { GlobalObjects, Partner } from '../../../models/global';
import { MeetingTypeModel } from '../../../models/meeting-types.model';
import { AppointmentState, UserState } from '../../../models/state.model';
import { CaptchaService } from '../../../services/captcha.service';
import { CountryHelperService } from '../../../services/country-helper.service';
import { CustomEventService } from '../../../services/custom-event.service';
import { FormsService } from '../../../services/forms.service';
import { LoggerService } from '../../../services/logger.service';
import { RefDataService } from '../../../services/ref-data.service';
import { isZipPrefilledFromStorePostcode } from '../../../services/store-postcode.util';
import { UtilService } from '../../../services/util.service';
import { CountrySelectComponent } from '../../../shared/components/country-select/country-select.component';
import { PhishingProtectionDirective } from '../../../shared/directive/app-phising-protection.directive';
import { AddressFormatPipe } from '../../../shared/pipes/address-format.pipe';
import { CalioAddRequiredPipe } from '../../../shared/pipes/calio-add-required.pipe';
import { CalioMeetingTemplatePipe } from '../../../shared/pipes/calio-meeting-template.pipe';
import { CalioTranslateResolverPipe } from '../../../shared/pipes/calio-translate-resolver.pipe';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../shared/pipes/trust-html.pipe';
import { CwCheckboxComponent } from '../theme/cw-checkbox/cw-checkbox.component';
import { CwIntlTelInputComponent } from '../theme/cw-intl-tel-input/cw-intl-tel-input.component';

const modules = [MatFormFieldModule, MatInputModule, MatDatepickerModule, NgSelectModule, FormsModule, TranslateModule, FontAwesomeModule];
const components = [CwIntlTelInputComponent, CountrySelectComponent, CwCheckboxComponent];
const pipes = [TranslationPipe, TrustHtmlPipe, CalioMeetingTemplatePipe, CalioAddRequiredPipe, CalioTranslateResolverPipe, AddressFormatPipe];
const directives = [PhishingProtectionDirective];

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [NgClass, NgTemplateOutlet, ...modules, ...components, ...pipes, ...directives],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }
  ]
})
export class CustomerFormComponent implements OnInit, AfterViewInit {

  private utilService = inject(UtilService);
  private formService = inject(FormsService);
  private translate = inject(TranslateService);
  private countryHelperService = inject(CountryHelperService);
  private refDataService = inject(RefDataService);
  private captchaService = inject(CaptchaService);
  private el = inject(ElementRef);
  private adapter = inject(DateAdapter);
  private customEventService = inject(CustomEventService);
  private destroyRef = inject(DestroyRef);

  @Input({
    transform: numberAttribute
  }) index: number;
  @Input() customerRequiredFields: string[] = [];
  @Input() customerFormFields: any[] = [];
  @Input() userState: UserState;
  @Input() globals: GlobalObjects;
  @Input() partner: Partner;
  @Input() userDataLSNameKey: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() type: string;
  @Input() lang: string;
  @Input() meetingTypes: MeetingTypeModel[];
  @Input() appointmentCart?: CartItem[] = [];
  @Input() notificationTypes: any[] = [];
  @Input() showBeforeCustomField = false;
  @Input() showAfterCustomField = false;
  @Input() appointmentState: AppointmentState;
  @Input() stores: StoresDao[] = [];
  @Input() customerSelected: CustomerDbModel;
  // The postcode the customer entered in the store search. While the zip field still holds this value
  // it carries the `cbw-zip-prefilled` class, which partners can target from their custom CSS.
  // Signal input for this new input only; the component keeps its decorators.
  lockedZip = input<string | null>(null);

  @ViewChild('phoneNumber') phoneNumberModel: NgModel;
  @ViewChild('mobileNumber') mobileNumberModel: NgModel;
  @ViewChild('smsPhoneCodeModel') smsPhoneCodeModel: NgModel;
  @ViewChild('smsMobileCodeModel') smsMobileCodeModel: NgModel;
  @ViewChild('captcha') captchaModel: NgModel;
  @ViewChild('friendlyCaptcha', { static: false }) friendlyCaptchaRef!: ElementRef;

  readonly salutationTypes = SALUTATION_TYPES;
  readonly titleTypes = CUSTOMER_CSTM_FIELD_TITLE_LIST;
  readonly meetingTypeId = MEETING_TYPE_ID;
  readonly zipRegex = REGEX.ZIP;
  readonly templateContent = TRANSLATION_TEMPLATES;
  protected readonly captchaType = CAPTCHA_TYPE;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  appointmentConstant = APPOINTMENT_CONSTANT;
  currentLanguage: string;
  environment: any;
  fieldTypes: { type: string, value: any }[] = [];
  showPhoneSmsCodeButton = false;
  showPhoneSmsCodeInputField = false;
  smsPhoneButtonDisabled = false;
  smaPhoneButtonLoading = false;
  smsPhoneCode: number;
  showPhoneNumberVerifiedMessage = false;
  showPhoneSmsCodeVerifiedMessage = false;
  showMobileSmsCodeButton = false;
  showMobileSmsCodeInputField = false;
  smsMobileButtonDisabled = false;
  smaMobileButtonLoading = false;
  smsMobileCode: number;
  showMobileNumberVerifiedMessage = false;
  showMobileSmsCodeVerifiedMessage = false;
  smsResendForPhone = false;
  smsResendForMobile = false;
  countries: CountryDbModel[] = [];
  selectedStore: StoresDao = null;
  validatingCaptchaChallenge = false
  captchaValid = false;
  protected maximumPossibleDateForDatePicker = new Date();
  friendlyCaptchaSDK = new FriendlyCaptchaSDK();
  captchaWidgetHandle: WidgetHandle;
  captchaLoaded = false;
  friendlyCaptchaValidationRequired = false;
  citySuggestions: ZipCodeItem[] = [];
  private searchZip$ = new Subject<string>();

  constructor() {
    this.environment = environment;
    this.translate.onLangChange.subscribe(language => {
      this.lang = language.lang;
      this.countries = this.countryHelperService.findCountriesByDefaultLanguage(this.countries, language.lang);
      this.adapter.setLocale(this.lang);
    });
    this.captchaService.friendlyCaptchaValidationRequired.subscribe(required => {
      if (required && this.friendlyCaptchaRef?.nativeElement) {
        this.friendlyCaptchaRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      this.friendlyCaptchaValidationRequired = required;
    });
  }

  ngOnInit(): void {
    // Load captcha only if widget type is other than events
    if (![WIDGET_CONSTANTS.EVENT, WIDGET_CONSTANTS.EVENTS].includes(this.type)) {
      this.checkForCaptcha();
    }

    if (this.customerFormFields?.length > 0) {
      const customFieldIds: number[] = [];
      this.fieldTypes = this.customerFormFields.map((item: any) => {
        if (typeof item === 'number') {
          customFieldIds.push(item);
          return { type: 'CUSTOM_FIELD', value: item };
        } else {
          return { type: item, value: item };
        }
      });
    }

    if (this.globals?.isInternal) {
      // when isInternal is true but internal_comment is disabled then
      // set internal_comment to null to be sent when it passed in URL
      if (
        this.partner?.widget_internal_comment_shown === 0 ||
        this.partner?.widget_internal_comment_shown === undefined
      ) {
        this.userState.internal_comment = null;
      }
    } else {
      // set internal_comment as null to prevent
      // it to be sent when it passed in URL and internal is false
      this.userState.internal_comment = null;
    }

    if (this.partner.widget_birthday_shown && this.partner.widget_birthday_minimum_age_18) {
      this.maximumPossibleDateForDatePicker = moment().subtract(18, 'years').toDate();
    }

    this.adapter.setLocale(this.lang);
    this.partner?.widget_country_shown === 1 && this.getCountries();
    this.meetingTypes?.length && this.manageDisable();
    this.findSelectedStore();
    this.setupZipCodeSearch();
  }

  private setupZipCodeSearch(): void {
    if (this.partner?.is_zip_codes_search_enabled === 1) {
      this.searchZip$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((zip: string) => {
          if (zip?.length >= 3) {
            return this.formService.getCitiesByZip(zip);
          }
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (cities) => this.citySuggestions = cities,
        error: () => this.citySuggestions = []
      });

      // Trigger search for predefined ZIP code
      if (this.userState?.zip?.length >= 3) {
        this.searchZip$.next(this.userState.zip);
      }
    }
  }

  onZipInput(zip: string): void {
    if (this.partner?.is_zip_codes_search_enabled === 1) {
      // Skip API call if value is from datalist selection (contains parentheses)
      if (zip && /\s+\(.+\)$/.test(zip)) {
        return;
      }
      this.searchZip$.next(zip);
    }
  }

  onZipChange(zip: string): void {
    this.onChangeUserData();
    this.onZipInput(zip);
  }

  protected isZipPrefilled(): boolean {
    return isZipPrefilledFromStorePostcode(this.userState?.zip, this.lockedZip());
  }

  onZipBlur(): void {
    if (this.userState.zip) {
      this.userState.zip = this.userState.zip.trim().toUpperCase().replace(/\s+/g, ' ');
    }
  }

  onZipSuggestionSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedValue = input.value;

    // Parse "ZIP (CityName)" format
    const match = selectedValue.match(/^(\S+)\s+\((.+)\)$/);
    if (match) {
      const zip = match[1];
      const cityName = match[2].trim();
      const selectedCity = this.citySuggestions.find(city => city.zip === zip && city.city18 === cityName);
      if (selectedCity) {
        this.userState.zip = selectedCity.zip;
        this.userState.city = selectedCity.city18;
      }
    }
  }

  ngAfterViewInit(): void {
    this.focusFirstElement();

    if (this.partner.is_captcha_feature_enabled && this.partner.captcha_type === CAPTCHA_TYPE.FRIENDLY) {
      this.userState.friendly_captcha_challenge = '';
      this.captchaWidgetHandle = this.friendlyCaptchaSDK.createWidget({
        element: this.friendlyCaptchaRef.nativeElement,
        sitekey: this.partner.friendly_captcha_sitekey,
        language: this.lang.split('_')[0]
      });

      // Success event
      this.captchaWidgetHandle.addEventListener('frc:widget.complete', (event: FRCWidgetCompleteEvent) => {
        // event.detail.response contains the solution
        this.userState.friendly_captcha_challenge = event.detail.response;
        this.friendlyCaptchaValidationRequired = false;
        LoggerService.log('Captcha solved!', event.detail.response);
      });

      // Error event
      this.captchaWidgetHandle.addEventListener('frc:widget.error', (event: FRCWidgetWidgetErrorEvent) => {
        // event.detail.error contains error info
        this.userState.friendly_captcha_challenge = '';
        this.friendlyCaptchaValidationRequired = false;
        LoggerService.error('Captcha error:', event.detail.error);
      });
    }
  }

  private focusFirstElement(): void {
    // Get all focusable form elements in the DOM within the current component's view
    const focusableElements: NodeListOf<HTMLElement> = this.el.nativeElement.querySelectorAll('input, textarea, ng-select');
    // Focus the first element found
    LoggerService.log('[Debug] Auto focus will be set to this element', focusableElements?.[0]);
    focusableElements?.[0]?.focus();
  }

  private checkForCaptcha(): void {
    this.appointmentState.captcha = undefined;
    this.userState.captcha_challenge = '';

    if (this.partner?.is_captcha_feature_enabled === 1 && this.partner?.captcha_type === CAPTCHA_TYPE.CALENSO_DEFAULT) {
      this.captchaService.generateCaptcha().subscribe({
        next: (data) => {
          if (data) {
            this.appointmentState.captcha = data;
            setTimeout(() => {
              this.captchaModel?.control.addAsyncValidators(this.captchaValidator());
            }, 500);
          }
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
    }
  }

  private captchaValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      this.validatingCaptchaChallenge = true;

      return this.captchaService.validateCaptcha(this.appointmentState.captcha.uuid, control.value).pipe(
        debounceTime(2000),
        switchMap(result => {
          control.markAsTouched();
          if (!result) {
            return of({ invalidCaptcha: true });
          }
          return of(null);
        }),
        finalize(() => this.validatingCaptchaChallenge = false)
      );
    };
  }

  private manageDisable(): void {
    this.meetingTypes.forEach(meeting => {
      meeting.disabled = this.isMeetingTypeDisabled(meeting);
    });
  }

  private findSelectedStore(): void {
    const selectedStoreId = this.appointmentState?.store;
    if (selectedStoreId) {
      this.selectedStore = this.stores.find(store => store.id === selectedStoreId);
    }
  }

  private getCountries(): void {
    this.refDataService.getCountries().subscribe({
      next: countries => {
        if (countries) {
          this.countries = this.countryHelperService.findCountriesByDefaultLanguage(countries, this.lang);

          // Set country in user state when country_id is prefill
          if (this.userState?.country_id) {
            this.userState.country = this.countries.find(country => country.id === this.userState.country_id);
            this.updateUserData();
          }
        }
      },
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  onChangeCountry(country: CountryDbModel): void {
    this.userState.country = country;
    this.updateUserData();
  }

  onChangeUserData(): void {
    this.updateUserData();
  }

  onMeetingTypeChange(meetingType: MeetingTypeModel): void {
    this.userState.meetingType = meetingType;
    this.onChangeUserData();
  }

  updateUserData(): void {
    this.userDataLSNameKey && this.utilService.updateLocalStorage(
      this.userDataLSNameKey, Object.assign({}, this.userState)
    );
  }

  onEmailChange(eMailModel: NgModel): void {
    if (!this.globals?.isInternal) {
      if (this.partner?.widget_email_shown === 1) {
        if (this.partner?.widget_email_required === 1) {
        } else {
          if (eMailModel.value === '' || eMailModel.value === null || eMailModel.value === undefined) {
            eMailModel.control.markAsUntouched();
          }
        }
      }
    }

    if (this.globals?.isInternal) {
      if (this.partner?.internal_widget_email_shown === 1) {
        if (this.partner?.internal_widget_email_required === 1) {
        } else {
          if (eMailModel.value === '' || eMailModel.value === null || eMailModel.value === undefined) {
            eMailModel.control.markAsUntouched();
          }
        }
      }
    }
    this.customEventService.emailChangedEvent.emit();
    this.updateUserData();
  }

  requestSmsCode(mobile: string, isResend: boolean, type: "phone" | "mobile"): void {
    this.formService.requestSmsCode(mobile, type).subscribe({
      next: result => {
        if (result?.requested) {
          if (type === "phone") {
            this.showPhoneSmsCodeInputField = true;
            this.showPhoneSmsCodeButton = true;
            this.smsPhoneButtonDisabled = true;
          } else if (type === "mobile") {
            this.showMobileSmsCodeInputField = true;
            this.showMobileSmsCodeButton = true;
            this.smsMobileButtonDisabled = true;
          }

          if (isResend) {
            if (type === 'phone') {
              this.smsResendForPhone = true;
            } else if (type === 'mobile') {
              this.smsResendForMobile = true;
            }

            setTimeout(() => {
              this.smsResendForPhone = false;
              this.smsResendForMobile = false;
            }, 5000);
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        // Rate limit exceeded
        if (error.status === HttpStatusCode.TooManyRequests) {
          const control = type === "phone" ? (this.smsPhoneCodeModel.control || this.phoneNumberModel.control) : (this.smsMobileCodeModel.control || this.mobileNumberModel.control);
          this.handleRateLimitError(control, error);
        } else if (error.status === HttpStatusCode.Conflict) {
          // Mobile or phone number already verified
          if (type === "phone") {
            this.smaPhoneButtonLoading = false;
            this.showPhoneNumberVerifiedMessage = true;
          } else if (type === "mobile") {
            this.smaMobileButtonLoading = false;
            this.showMobileNumberVerifiedMessage = true;
          }

          this.updateUserData();
        } else if (error.status === HttpStatusCode.Forbidden) {
          // partner setting disabled
          if (type === "phone") {
            this.phoneNumberModel.control.markAsTouched();
            this.phoneNumberModel.control.setErrors({ sufficientPermissionsMissing: true });
          } else if (type === "mobile") {
            this.mobileNumberModel.control.markAsTouched();
            this.mobileNumberModel.control.setErrors({ sufficientPermissionsMissing: true });
          }
        } else if (error.status === HttpStatusCode.UnprocessableEntity) {
          // Retry limit exceeded
          if (isResend) {
            if (type === "phone") {
              this.smsPhoneCodeModel.control.markAsTouched();
              this.smsPhoneCodeModel.control.setErrors({ exceededSmsLimits: true });
            } else if (type === "mobile") {
              this.smsMobileCodeModel.control.markAsTouched();
              this.smsMobileCodeModel.control.setErrors({ exceededSmsLimits: true });
            }
          } else {
            if (type === "phone") {
              this.phoneNumberModel.control.setErrors({ exceededSmsLimits: true });
            } else if (type === "mobile") {
              this.mobileNumberModel.control.setErrors({ exceededSmsLimits: true });
            }
          }
        }
      }
    });
  };

  onBlurPhoneNumber(type: "phone" | "mobile"): void {
    if (type === "phone") {
      this.showPhoneSmsCodeButton = false;
      this.showPhoneSmsCodeInputField = false;
      this.smsPhoneButtonDisabled = false;
      this.showPhoneNumberVerifiedMessage = false;
      this.showPhoneSmsCodeVerifiedMessage = false;
      this.smsPhoneCode = undefined;
    } else if (type === "mobile") {
      this.showMobileSmsCodeButton = false;
      this.showMobileSmsCodeInputField = false;
      this.smsMobileButtonDisabled = false;
      this.showMobileNumberVerifiedMessage = false;
      this.showMobileSmsCodeVerifiedMessage = false;
      this.smsMobileCode = undefined;
    }

    if (
      type === "phone" &&
      this.userState.phone &&
      this.phoneNumberModel.errors?.invalidNumber === undefined &&
      this.partner.activate_phone_number_verification_feature === 1
    ) {
      this.verifyPhoneNumber(this.userState.phone, type);
    }

    if (
      type === "mobile" &&
      this.userState.mobile &&
      this.mobileNumberModel.errors?.invalidNumber === undefined &&
      this.partner.activate_mobile_number_verification_feature === 1
    ) {
      this.verifyPhoneNumber(this.userState.mobile, type);
    }
  }

  verifyPhoneNumber(mobile: string, type: "phone" | "mobile"): void {
    if (type === "phone") {
      this.showPhoneSmsCodeButton = false;
      this.smaPhoneButtonLoading = true;
      this.smsPhoneCode = undefined;
    } else if (type === "mobile") {
      this.showMobileSmsCodeButton = false;
      this.smaMobileButtonLoading = true;
      this.smsMobileCode = undefined;
    }
    this.formService.verifyPhoneNumber(mobile, type).subscribe({
      next: result => {
        if (result?.verified) {
          if (type === "phone") {
            this.smaPhoneButtonLoading = false;
            this.showPhoneNumberVerifiedMessage = true;
          } else if (type === "mobile") {
            this.smaMobileButtonLoading = false;
            this.showMobileNumberVerifiedMessage = true;
          }

          this.updateUserData();
        } else {
          if (type === "phone") {
            this.smaPhoneButtonLoading = false;
            this.phoneNumberModel.control.setErrors({ verficationNeeded: true });
            this.showPhoneSmsCodeButton = true;
            this.smsPhoneButtonDisabled = false;
          } else if (type === "mobile") {
            this.smaMobileButtonLoading = false;
            this.mobileNumberModel.control.setErrors({ verficationNeeded: true });
            this.showMobileSmsCodeButton = true;
            this.smsMobileButtonDisabled = false;
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        this.smaPhoneButtonLoading = false;
        this.smaMobileButtonLoading = false;
        if (error.status === HttpStatusCode.Forbidden) {
          // partner setting disabled
          if (type === "phone") {
            this.phoneNumberModel.control.markAsTouched();
            this.phoneNumberModel.control.setErrors({ sufficientPermissionsMissing: true });
          } else if (type === "mobile") {
            this.mobileNumberModel.control.markAsTouched();
            this.mobileNumberModel.control.setErrors({ sufficientPermissionsMissing: true });
          }
        }
      }
    });
  }

  confirmSmsCode(mobile: string, verification_code: number, type: "phone" | "mobile"): void {
    this.formService.confirmSmsCode(mobile, verification_code, type).subscribe({
      next: (result: { verified: boolean, count: number }) => {
        if (result?.verified) {
          if (type === "phone") {
            this.showPhoneSmsCodeVerifiedMessage = true;
            this.phoneNumberModel.control.setValidators([]);
            this.phoneNumberModel.control.updateValueAndValidity();
            this.smsPhoneCodeModel.control.setValidators([]);
            this.smsPhoneCodeModel.control.updateValueAndValidity();
            this.showPhoneSmsCodeInputField = false;
            this.showPhoneSmsCodeButton = false;
            this.smsPhoneCode = undefined;
          } else if (type === "mobile") {
            this.showMobileSmsCodeVerifiedMessage = true;
            this.mobileNumberModel.control.setValidators([]);
            this.mobileNumberModel.control.updateValueAndValidity();
            this.smsMobileCodeModel.control.setValidators([]);
            this.smsMobileCodeModel.control.updateValueAndValidity();
            this.showMobileSmsCodeInputField = false;
            this.showMobileSmsCodeButton = false;
            this.smsMobileCode = undefined;
          }
        } else {
          if (type === "phone") {
            this.smsPhoneCodeModel.control.setErrors({ validCodeNeeded: true });
          } else if (type === "mobile") {
            this.smsMobileCodeModel.control.setErrors({ validCodeNeeded: true });
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.Forbidden) {
          // partner setting disabled
          if (type === "phone") {
            this.smsPhoneCodeModel.control.markAsTouched();
            this.smsPhoneCodeModel.control.setErrors({ sufficientPermissionsMissing: true });
          } else if (type === "mobile") {
            this.smsMobileCodeModel.control.markAsTouched();
            this.smsMobileCodeModel.control.setErrors({ sufficientPermissionsMissing: true });
          }
        }
      }
    });
  }

  smsCodeVerification(event: KeyboardEvent, type: "phone" | "mobile"): boolean {
    const numReg = new RegExp('^[0-9]$');
    if (numReg.test(event.key)) {
      if (this.smsPhoneCode && (this.smsPhoneCode + '').length >= 6 && type === 'phone') {
        return false;
      } else if (this.smsMobileCode && (this.smsMobileCode + '').length >= 6 && type === 'mobile') {
        return false;
      }
      return true;
    }
    // Only ASCII charactar in that range allowed
    const ASCIICode = (event.which) ? event.which : event.keyCode;

    if (ASCIICode === 8) {
      return true;
    }

    if (ASCIICode === 65 && event.metaKey) {
      return true;
    }

    return false;
  }

  isMeetingTypeDisabled(meetingType: MeetingTypeModel): boolean {
    if (
      this.type === APPOINTMENT_CONSTANT.APPOINTMENT &&
      meetingType.id === 10 &&
      this.appointmentCart?.length
    ) {
      for (const cartItem of this.appointmentCart) {
        const duration = Math.round(cartItem.total.duration * 60);

        if (duration < 10) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Checks if the selected meeting type is an online meeting provider.
   * Used to automatically show and require the email field for online meetings.
   */
  isOnlineMeetingType(): boolean {
    const onlineMeetingTypes = [
      MEETING_TYPE_ID.ZOOM,
      MEETING_TYPE_ID.UNBLU,
      MEETING_TYPE_ID.MICROSOFT_TEAMS,
      MEETING_TYPE_ID.GOOGLE_MEET,
      MEETING_TYPE_ID.GOTO_MEETING,
      MEETING_TYPE_ID.WEBEX,
      MEETING_TYPE_ID.METAVERSE,
      MEETING_TYPE_ID.KMEET
    ];
    return onlineMeetingTypes.includes(this.userState?.meeting_type_id);
  }

  /**
   * Checks if the selected meeting type requires a phone number.
   * Used to automatically show both phone and mobile fields.
   */
  isPhoneMeetingType(): boolean {
    return [MEETING_TYPE_ID.PHONE, MEETING_TYPE_ID.WILL_CALL_YOU]
      .includes(this.userState?.meeting_type_id);
  }

  // Admin choice first. Safety net only when both phone and mobile are disabled
  // on a phone/callback meeting, so the customer still has a way to be contacted.
  isPhoneFieldVisible(): boolean {
    if (this.partner?.needPhoneNumber) {
      return true;
    }
    return this.isPhoneMeetingType()
      && !this.partner?.needPhoneNumber
      && !this.partner?.needMobileNumber;
  }

  isMobileFieldVisible(): boolean {
    if (this.partner?.needMobileNumber) {
      return true;
    }
    return this.isPhoneMeetingType()
      && !this.partner?.needPhoneNumber
      && !this.partner?.needMobileNumber;
  }

  // Admin explicit flags first. For phone/callback meetings the widget guarantees
  // at least one phone field is required: if phone is the only visible field it
  // stays required; if mobile is visible and the admin already marked it required,
  // phone stays optional; otherwise mutual fallback requires whichever is empty.
  isPhoneFieldRequired(): boolean {
    if (this.partner?.widget_phone_required === 1) {
      return true;
    }
    if (this.customerRequiredFields?.indexOf('phone') > -1) {
      return true;
    }
    if (!this.isPhoneMeetingType()) {
      return false;
    }
    if (!this.isMobileFieldVisible()) {
      return true;
    }
    const mobileExplicitlyRequired = this.partner?.widget_mobile_required === 1
      || this.customerRequiredFields?.indexOf('mobile') > -1;
    if (mobileExplicitlyRequired) {
      return false;
    }
    return !this.userState?.mobile;
  }

  isMobileFieldRequired(): boolean {
    if (this.partner?.widget_mobile_required === 1) {
      return true;
    }
    if (this.customerRequiredFields?.indexOf('mobile') > -1) {
      return true;
    }
    if (!this.isPhoneMeetingType()) {
      return false;
    }
    if (!this.isPhoneFieldVisible()) {
      return true;
    }
    const phoneExplicitlyRequired = this.partner?.widget_phone_required === 1
      || this.customerRequiredFields?.indexOf('phone') > -1;
    if (phoneExplicitlyRequired) {
      return false;
    }
    return !this.userState?.phone;
  }

  toggleDataPolicy(event: any): void {
    this.userState.acceptDataPolicy = event.event.target.checked;
  }

  private handleRateLimitError(control: FormControl<any>, error: HttpErrorResponse): void {
    let blockForTimeMinutes = 0;
    let blockForTimeSeconds = 0;

    if (error.headers.get('Retry-After')) {
      const retryAfter = +error.headers.get('Retry-After');
      if (retryAfter > 60) {
        blockForTimeMinutes = Math.floor(retryAfter / 60);
      } else {
        blockForTimeSeconds = +error.headers.get('Retry-After');
      }
    }

    control.markAsTouched();
    control.setErrors({ tooManyRequests: { blockForTimeMinutes, blockForTimeSeconds } });
  }
}
